import { supabase } from '../lib/supabase';

// Service layer for Cash Flow Forecast operations
export const cashFlowService = {
  /**
   * Retry helper for handling schema cache issues
   * Implements exponential backoff for PGRST205 errors
   */
  async retryWithBackoff(operation, maxRetries = 3) {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        // Check if this is a schema cache error (PGRST205)
        const isCacheError = error?.code === 'PGRST205' || error?.message?.includes('schema cache');
        
        // Only retry for cache errors and if we haven't exceeded max retries
        if (isCacheError && attempt < maxRetries - 1) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 5000);
          console.warn(`Schema cache error detected, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        
        throw error;
      }
    }
  },

  /**
   * Get or create cash flow forecast for current user
   * Returns existing forecast or creates new one if doesn't exist
   */
  async getOrCreateForecast() {
    return this.retryWithBackoff(async () => {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Call database function to get or create forecast
      const { data, error } = await supabase?.rpc('get_or_create_cash_flow_forecast', { p_user_id: user?.id });

      if (error) throw error;

      // Get the full forecast data
      const { data: forecast, error: fetchError } = await supabase?.from('cash_flow_forecasts')?.select('*')?.eq('id', data)?.single();

      if (fetchError) throw fetchError;

      // Convert snake_case to camelCase
      return this.convertToReactFormat(forecast);
    });
  },

  /**
   * Load forecast data for current user
   */
  async loadForecast() {
    return this.retryWithBackoff(async () => {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('cash_flow_forecasts')?.select('*')?.eq('user_id', user?.id)?.single();

      if (error) {
        // If no forecast exists, return null (not an error)
        if (error?.code === 'PGRST116') return null;
        throw error;
      }

      return this.convertToReactFormat(data);
    });
  },

  /**
   * Save/update forecast data
   */
  async saveForecast(forecastData) {
    return this.retryWithBackoff(async () => {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Convert camelCase to snake_case for database
      const dbData = this.convertToDatabaseFormat(forecastData);

      // Check if forecast exists
      const { data: existing } = await supabase?.from('cash_flow_forecasts')?.select('id')?.eq('user_id', user?.id)?.single();

      let result;
      if (existing) {
        // Update existing
        const { data, error } = await supabase?.from('cash_flow_forecasts')?.update({
            ...dbData,
            user_id: user?.id
          })?.eq('id', existing?.id)?.select()?.single();

        if (error) throw error;
        result = data;
      } else {
        // Create new
        const { data, error } = await supabase?.from('cash_flow_forecasts')?.insert({
            ...dbData,
            user_id: user?.id
          })?.select()?.single();

        if (error) throw error;
        result = data;
      }

      return this.convertToReactFormat(result);
    });
  },

  /**
   * Get effective user ID for all queries
   */
  getEffectiveUserId() {
    const impersonationData = localStorage.getItem('admin_impersonation');
    if (impersonationData) {
      const { impersonatedUserId } = JSON.parse(impersonationData);
      return impersonatedUserId;
    }
    return null;
  },

  /**
   * Get all forecasts for current user
   */
  async getForecasts() {
    try {
      const effectiveUserId = this.getEffectiveUserId();
      
      let query = supabase
        ?.from('cash_flow_forecasts')
        ?.select('*')
        ?.order('created_at', { ascending: false });
      
      if (effectiveUserId) {
        query = query?.eq('user_id', effectiveUserId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data?.map(item => this.convertToReactFormat(item)) || [];
    } catch (error) {
      console.error('Error fetching cash flow forecasts:', error);
      throw error;
    }
  },

  /**
   * Convert database format (snake_case) to React format (camelCase)
   */
  convertToReactFormat(dbData) {
    if (!dbData) return null;

    return {
      id: dbData?.id,
      userId: dbData?.user_id,
      currency: dbData?.currency,
      currentCash: dbData?.current_cash || '',
      monthlyRevenue: dbData?.monthly_revenue || '',
      revenueGrowthRate: dbData?.revenue_growth_rate || 5,
      productCost: dbData?.product_cost || '',
      quantityNeeded: dbData?.quantity_needed || '',
      supplierPaymentTerms: dbData?.supplier_payment_terms || 30,
      leadTimeDays: dbData?.lead_time_days || 14,
      safetyStockPercent: dbData?.safety_stock_percent || 20,
      campaignBudgets: {
        social: dbData?.campaign_budget_social || '',
        search: dbData?.campaign_budget_search || '',
        display: dbData?.campaign_budget_display || '',
        email: dbData?.campaign_budget_email || ''
      },
      monthlyMarketingSpend: dbData?.monthly_marketing_spend || '',
      customerAcquisitionCost: dbData?.customer_acquisition_cost || '',
      conversionRate: dbData?.conversion_rate || 2.5,
      monthlyOperatingCosts: dbData?.monthly_operating_costs || '',
      staffCosts: dbData?.staff_costs || '',
      overheadCosts: dbData?.overhead_costs || '',
      scenarioResults: dbData?.scenario_results || [],
      createdAt: dbData?.created_at,
      updatedAt: dbData?.updated_at
    };
  },

  /**
   * Convert React format (camelCase) to database format (snake_case)
   */
  convertToDatabaseFormat(reactData) {
    return {
      currency: reactData?.currency,
      current_cash: parseFloat(reactData?.currentCash) || 0,
      monthly_revenue: parseFloat(reactData?.monthlyRevenue) || 0,
      revenue_growth_rate: parseFloat(reactData?.revenueGrowthRate) || 5,
      product_cost: parseFloat(reactData?.productCost) || 0,
      quantity_needed: parseInt(reactData?.quantityNeeded) || 0,
      supplier_payment_terms: parseInt(reactData?.supplierPaymentTerms) || 30,
      lead_time_days: parseInt(reactData?.leadTimeDays) || 14,
      safety_stock_percent: parseFloat(reactData?.safetyStockPercent) || 20,
      campaign_budget_social: parseFloat(reactData?.campaignBudgets?.social) || 0,
      campaign_budget_search: parseFloat(reactData?.campaignBudgets?.search) || 0,
      campaign_budget_display: parseFloat(reactData?.campaignBudgets?.display) || 0,
      campaign_budget_email: parseFloat(reactData?.campaignBudgets?.email) || 0,
      monthly_marketing_spend: parseFloat(reactData?.monthlyMarketingSpend) || 0,
      customer_acquisition_cost: parseFloat(reactData?.customerAcquisitionCost) || 0,
      conversion_rate: parseFloat(reactData?.conversionRate) || 2.5,
      monthly_operating_costs: parseFloat(reactData?.monthlyOperatingCosts) || 0,
      staff_costs: parseFloat(reactData?.staffCosts) || 0,
      overhead_costs: parseFloat(reactData?.overheadCosts) || 0,
      scenario_results: reactData?.scenarioResults || []
    };
  }
};